/**
 * Server-sent-events subscription against the CC server (`/api/v1/stream`).
 *
 * Ported from the legacy CC `useSSE` and extended with the V3.1 fleet
 * event types (`signal.emitted`, `signal.result`, `agent.health`,
 * `assignment.changed`) so fleet pages update in realtime. Related query
 * keys are invalidated automatically; page-specific handlers are optional.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { SseEventType } from "../api/fleet";

type Handler = (data: Record<string, unknown>) => void;

const EVENT_TYPES: SseEventType[] = [
  "bot.status",
  "bot.stats",
  "bot.event",
  "command.ack",
  "config.activated",
  "notification.fired",
  "signal.emitted",
  "signal.result",
  "agent.health",
  "assignment.changed",
];

export function useSSE(handlers: Partial<Record<SseEventType, Handler>>) {
  const qc = useQueryClient();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      es = new EventSource("/api/v1/stream");

      for (const type of EVENT_TYPES) {
        es.addEventListener(type, (e: MessageEvent) => {
          let data: Record<string, unknown> = {};
          try {
            data = JSON.parse(e.data as string) as Record<string, unknown>;
          } catch {
            /* ignore malformed frames */
          }
          const agentId = typeof data.agent_id === "string" ? data.agent_id : undefined;

          if (type === "bot.status" || type === "bot.stats" || type === "agent.health") {
            void qc.invalidateQueries({ queryKey: ["fleet", "agents"] });
            if (agentId) {
              void qc.invalidateQueries({ queryKey: ["fleet", "agent", agentId] });
            }
          }
          if (type === "command.ack" && agentId) {
            void qc.invalidateQueries({ queryKey: ["fleet", "agent", agentId, "commands"] });
          }
          if (type === "signal.emitted" || type === "signal.result") {
            void qc.invalidateQueries({ queryKey: ["fleet", "signals"] });
            if (agentId) {
              void qc.invalidateQueries({ queryKey: ["fleet", "agent", agentId, "actions"] });
            }
          }
          if (type === "assignment.changed") {
            void qc.invalidateQueries({ queryKey: ["fleet", "assignments"] });
          }
          handlersRef.current[type]?.(data);
        });
      }

      es.onerror = () => {
        es?.close();
        es = null;
        if (!disposed) {
          retryTimer = setTimeout(connect, 5000);
        }
      };
    };

    connect();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      es?.close();
    };
  }, [qc]);
}
